import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card, EmptyState } from '@timely/ui';
import { useActiveWorkspace } from '../workspace/WorkspaceProvider';
import { Page } from '../workspace/WorkspaceShell';
import { useOnboarding } from '../onboarding/useOnboarding';
import { useSetupStatus, type SetupItem } from '../onboarding/useSetupStatus';

/**
 * The onboarding checklist: what a brand-new company sees.
 *
 * Every "done" here is a real question answered against the database — a
 * connected, synced data source; how many people have joined; whether an AI
 * key is saved — never a flag this screen invented. `Today` shows this
 * instead of the day's figures only while the mandatory step (a usable
 * receivables source) is not yet done *and this company has never finished
 * onboarding before* (`useOnboarding`); once it has, an empty book later —
 * after a Fresh Start, say — gets a plain empty state below, not this
 * checklist again.
 */
export function WorkspaceHome() {
    const context = useActiveWorkspace();
    const { workspace, membership } = context;
    const onboarding = useOnboarding(workspace.id);
    const { items, loading, mandatoryDone } = useSetupStatus(context);

    // The moment mandatory setup is first true, record it — once, durably —
    // so this checklist does not reappear the next time the book is empty
    // for an ordinary reason.
    useEffect(() => {
        if (mandatoryDone && !onboarding.completedAt && !onboarding.loading) {
            void onboarding.markComplete();
        }
    }, [mandatoryDone, onboarding.completedAt, onboarding.loading, onboarding.markComplete]);

    const required = items.filter(i => i.required);
    const optional = items.filter(i => !i.required);

    return (
        <Page>
            <h2 className="text-[26px] font-bold text-label tracking-[-0.03em]">
                Welcome to Timely Payment
            </h2>
            <p className="text-[15px] text-label-2 mt-2 leading-relaxed">
                {workspace.name} is set up and you are its {membership.roleName.toLowerCase()}.
                Here is what is left.
            </p>

            <Card className="mt-7 divide-y divide-separator overflow-hidden">
                {required.map(item => <ChecklistRow key={item.area} item={item} loading={loading} primary />)}
            </Card>

            {!loading && optional.length > 0 && (
                <>
                    <h2 className="text-[13px] font-semibold text-label-3 uppercase tracking-wide mt-8 mb-2">
                        Optional — do these whenever you like
                    </h2>
                    <Card className="divide-y divide-separator overflow-hidden">
                        {optional.map(item => <ChecklistRow key={item.area} item={item} loading={loading} />)}
                    </Card>
                    <p className="text-[12.5px] text-label-3 mt-3">
                        <Link to={`/w/${workspace.slug}/setup`} className="hover:underline">
                            See the full company setup status
                        </Link>
                    </p>
                </>
            )}

            <Card className="mt-6">
                <EmptyState
                    title="No customers yet"
                    hint="Connect your receivables and your book appears here. Nothing is invented in the meantime — an empty company is empty."
                />
            </Card>
        </Page>
    );
}

function ChecklistRow({ item, loading, primary }: { item: SetupItem; loading: boolean; primary?: boolean }) {
    const done = item.state === 'configured';
    const attention = item.state === 'needs_attention';
    const badge = loading ? 'bg-card-3 text-label-3'
        : done ? 'bg-pos-bg text-pos' : attention ? 'bg-warn-bg text-warn' : 'bg-card-3 text-label-3';

    return (
        <div className="px-5 py-4 flex items-start gap-3.5">
            <span
                className={`w-6 h-6 rounded-full grid place-items-center text-[12px] font-bold flex-none mt-0.5 ${badge}`}
                aria-hidden="true"
            >
                {loading ? '·' : done ? '✓' : attention ? '!' : primary ? '○' : ''}
            </span>
            <div className="min-w-0">
                <div className="text-[14.5px] font-semibold text-label">
                    {item.can && item.href ? (
                        <Link to={item.href} className="text-accent hover:underline">{item.label}</Link>
                    ) : (
                        item.label
                    )}
                </div>
                <div className="text-[13px] text-label-3 mt-0.5 leading-snug">
                    {loading ? 'Checking…' : item.detail}
                </div>
            </div>
        </div>
    );
}
